'use client';

import { Button, Icons, Loading } from '@commenda/kakashi';
import {
  ComponentTemplate,
  FormComponent,
  FormModule,
  FormsTemplateStatus,
} from '@commenda/types';
import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  getFirstCollision,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { GripVertical, Layers } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  FORM_BUILDER_DROP_ANIMATION,
  ResizablePanelDirection,
} from '../constants';
import { FormCanvas } from '../FormCanvas';
import { useFormEditor } from '../hooks/useFormEditor';
import { ResizeHandle, useResizablePanels } from '../hooks/useResizablePanels';
import { PropertyEditor } from '../PropertyEditor';
import { generateId, isModuleLockingComponent } from '../types';
import { ComponentPalette } from './ComponentPalette';
import FormBuilderHeader from './FormBuilderHeader';
import { AddStepDialog } from './FormCanvas/AddStepDialog';
import { ResetTemplateDialog } from './Header/ResetTemplateDialog';
import SaveTemplateDialog from './Header/SaveTemplateDialog';

// ---------- Types & helpers specific to this file ----------

type StatusBadgeVariant = 'gray' | 'success' | 'warning';

type DraggedItem =
  | {
      type: 'new-component';
      template: ComponentTemplate;
    }
  | {
      type: 'component';
      component: FormComponent;
    }
  | {
      type: 'module';
      module: FormModule;
    }
  | {
      type: 'step';
      step: { id: string; title: string; order: number };
    }
  | {
      type: 'saved-module';
      savedModule: { id: string; name: string };
    };

const getStatusBadgeVariant = (
  status: FormsTemplateStatus
): StatusBadgeVariant => {
  switch (status) {
    case FormsTemplateStatus.LIVE:
      return 'success';
    case FormsTemplateStatus.DRAFT:
      return 'warning';
    case FormsTemplateStatus.COMPLETED:
    default:
      return 'gray';
  }
};

// ---------- Drag overlay component ----------

function DragOverlayContent({
  draggedItem,
}: {
  draggedItem: DraggedItem | null;
}) {
  if (!draggedItem) return null;

  if (draggedItem.type === 'new-component') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-border-brand bg-bg-primary shadow-lg opacity-90">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-brand-subtle">
          <Icons.Plus className="w-4 h-4 text-text-brand" />
        </div>
        <div>
          <p className="txt-small-medium text-text-primary">
            {draggedItem.template.name}
          </p>
        </div>
      </div>
    );
  }

  if (draggedItem.type === 'component') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-border-brand bg-bg-primary shadow-lg opacity-90">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-tertiary">
          <Icons.Edit03 className="w-4 h-4 text-text-secondary" />
        </div>
        <div>
          <p className="txt-small-medium text-text-primary">
            {draggedItem.component.label || draggedItem.component.component}
          </p>
        </div>
      </div>
    );
  }

  if (draggedItem.type === 'module') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-lg border border-border-brand bg-bg-primary shadow-lg opacity-90">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-tertiary">
          <Layers className="w-4 h-4 text-text-secondary" />
        </div>
        <div>
          <p className="txt-small-medium text-text-primary">
            {draggedItem.module.name}
          </p>
          {draggedItem.module.description && (
            <p className="txt-xs-regular text-text-tertiary">
              {draggedItem.module.description}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (draggedItem.type === 'step') {
    return (
      <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-brand text-text-inverse shadow-lg opacity-90">
        <GripVertical className="w-3 h-3 opacity-50" />
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-current/20 txt-xs-medium">
          {draggedItem.step.order}
        </span>
        <span className="txt-small-medium">{draggedItem.step.title}</span>
      </div>
    );
  }

  // saved-module
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border-brand bg-bg-primary shadow-lg opacity-90">
      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-bg-brand-subtle">
        <Layers className="w-4 h-4 text-text-brand" />
      </div>
      <div>
        <p className="txt-small-medium text-text-primary">
          {draggedItem.savedModule.name}
        </p>
        <p className="txt-xs-regular text-text-tertiary">Saved Module</p>
      </div>
    </div>
  );
}

// ---------- Shell: DnD + layout + children components ----------

export function FormBuilderEditorShell() {
  const {
    formBuilder,
    templateId,
    isMounted,
    readOnly,
    isLoadingTemplate,
    templateError,

    activeStepIndex,
    setActiveStepIndex,

    showPreview,
    setShowPreview,
    showResetConfirm,
    setShowResetConfirm,
    showSaveDialog,
    setShowSaveDialog,
    showAddStepDialog,
    setShowAddStepDialog,

    saveDialogData,
    setSaveDialogData,

    fileInputRef,
    handleImport,

    handleQuickSave,
    handleSaveTemplate,
    handleGoToTemplates,
    handleGoToKeys,

    handleAddStepClick,
    handleAddNewStep,
    handleAddSavedStep,

    resetFormAndClearTemplateId,
  } = useFormEditor();

  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [newlyAddedComponentId, setNewlyAddedComponentId] = useState<
    string | null
  >(null);
  const [newlyAddedModuleId, setNewlyAddedModuleId] = useState<string | null>(
    null
  );
  const [isAddingNewComponent, setIsAddingNewComponent] = useState(false);

  // Resizable panels
  const { leftWidth, rightWidth, handleMouseDown } = useResizablePanels();

  // sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // collision detection
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const { active } = args;
    const activeData = active.data.current as any;

    if (activeData?.type === 'component') {
      const closestCollisions = closestCenter(args);

      const componentCollision = closestCollisions.find(
        (collision) =>
          collision.data?.droppableContainer?.data?.current?.type ===
          'component'
      );
      if (componentCollision) return [componentCollision];

      const moduleCollision = closestCollisions.find(
        (collision) =>
          collision.data?.droppableContainer?.data?.current?.type === 'module'
      );
      if (moduleCollision) return [moduleCollision];

      return closestCollisions;
    }

    const pointerCollisions = pointerWithin(args);
    const moduleCollision = pointerCollisions.find(
      (collision) =>
        collision.data?.droppableContainer?.data?.current?.type === 'module'
    );
    if (moduleCollision) return [moduleCollision];

    const rectCollisions = rectIntersection(args);
    const moduleRectCollision = rectCollisions.find(
      (collision) =>
        collision.data?.droppableContainer?.data?.current?.type === 'module'
    );
    if (moduleRectCollision) return [moduleRectCollision];

    const closestCollisions = closestCenter(args);
    const firstCollision = getFirstCollision(closestCollisions);
    return firstCollision ? [firstCollision] : closestCollisions;
  }, []);

  // scroll and clear highlights
  useEffect(() => {
    if (!newlyAddedComponentId) {
      return undefined;
    }
    const el = document.querySelector(
      `[data-component-id="${newlyAddedComponentId}"]`
    );
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const t = setTimeout(() => setNewlyAddedComponentId(null), 1500);
    return () => clearTimeout(t);
  }, [newlyAddedComponentId]);

  useEffect(() => {
    if (!newlyAddedModuleId) {
      return undefined;
    }
    const el = document.querySelector(
      `[data-module-id="${newlyAddedModuleId}"]`
    );
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const t = setTimeout(() => setNewlyAddedModuleId(null), 1500);
    return () => clearTimeout(t);
  }, [newlyAddedModuleId]);

  // DnD handlers
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = active.data.current as any;

    if (data?.type === 'new-component') {
      setIsAddingNewComponent(true);
      setDraggedItem({
        type: 'new-component',
        template: data.template as ComponentTemplate,
      });
      return;
    }

    if (data?.type === 'saved-module') {
      setIsAddingNewComponent(true);
      setDraggedItem({
        type: 'saved-module',
        savedModule: {
          id: data.moduleId as string,
          name: data.moduleName as string,
        },
      });
      return;
    }

    if (data?.type === 'component') {
      setIsAddingNewComponent(false);
      setDraggedItem({
        type: 'component',
        component: data.component as FormComponent,
      });
      return;
    }

    if (data?.type === 'module') {
      setIsAddingNewComponent(false);
      setDraggedItem({
        type: 'module',
        module: data.module as FormModule,
      });
      return;
    }

    if (data?.type === 'step') {
      setIsAddingNewComponent(false);
      setDraggedItem({
        type: 'step',
        step: {
          id: data.step.id as string,
          title: data.step.title as string,
          order: data.step.order as number,
        },
      });
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setDraggedItem(null);

      if (!over) {
        setIsAddingNewComponent(false);
        return;
      }

      const activeData = active.data.current as any;
      const overData = over.data.current as any;

      // new component -> module
      if (activeData?.type === 'new-component' && overData?.type === 'module') {
        const componentTemplate = activeData.template as ComponentTemplate;
        const stepId = overData.stepId as string;
        const moduleId = overData.moduleId as string;

        // NestedForm always gets its own new module (atomic: avoids stale state)
        if (isModuleLockingComponent(componentTemplate.type)) {
          const result = formBuilder.addModuleWithSingleComponent(stepId, {
            ...componentTemplate.defaultConfig,
            fieldId: undefined,
          });
          if (result) {
            formBuilder.setSelection({
              type: 'component',
              stepId,
              moduleId: result.moduleId,
              componentId: result.componentId,
            });
            if (isAddingNewComponent) {
              setTimeout(() => {
                setNewlyAddedComponentId(result.componentId);
                setIsAddingNewComponent(false);
              }, 50);
            }
          } else {
            setIsAddingNewComponent(false);
          }
          return;
        }

        // Block drops onto NestedForm-locked modules
        const targetModuleForDrop = formBuilder.dsl.steps
          .find((s) => s.id === stepId)
          ?.modules.find((m) => m.id === moduleId);
        const isTargetLocked =
          targetModuleForDrop != null &&
          targetModuleForDrop.components.length === 1 &&
          isModuleLockingComponent(targetModuleForDrop.components[0].component);
        if (isTargetLocked) {
          setIsAddingNewComponent(false);
          return;
        }

        const newComponentId = formBuilder.addComponent(stepId, moduleId, {
          ...componentTemplate.defaultConfig,
          id: generateId('component'),
          fieldId: undefined,
        });

        if (newComponentId) {
          formBuilder.setSelection({
            type: 'component',
            stepId,
            moduleId,
            componentId: newComponentId,
          });

          if (isAddingNewComponent) {
            setTimeout(() => {
              setNewlyAddedComponentId(newComponentId);
              setIsAddingNewComponent(false);
            }, 50);
          }
        }
        return;
      }

      // saved module -> module or component
      if (activeData?.type === 'saved-module') {
        const moduleData = activeData.moduleData as
          | { dsl?: { components?: FormComponent[] } }
          | undefined;
        const dsl = moduleData?.dsl as
          | { components?: FormComponent[] }
          | undefined;

        const components = dsl?.components || [];
        const stepId =
          (overData?.stepId as string | undefined) ??
          (activeData.stepId as string | undefined);

        if (!stepId) {
          setIsAddingNewComponent(false);
          return;
        }

        const newModuleId = formBuilder.addModuleFromTemplate(stepId, {
          name: activeData.moduleName,
          description: undefined,
          components,
        });

        if (newModuleId) {
          formBuilder.setSelection({
            type: 'module',
            stepId,
            moduleId: newModuleId,
          });

          if (isAddingNewComponent) {
            setTimeout(() => {
              setNewlyAddedModuleId(newModuleId);
              setIsAddingNewComponent(false);
            }, 50);
          }
        } else {
          setIsAddingNewComponent(false);
        }
        return;
      }

      // new component -> component (insert after)
      if (
        activeData?.type === 'new-component' &&
        overData?.type === 'component'
      ) {
        const componentTemplate = activeData.template as ComponentTemplate;
        const stepId = overData.stepId as string;
        const moduleId = overData.moduleId as string;

        // NestedForm always gets its own new module (atomic: avoids stale state)
        if (isModuleLockingComponent(componentTemplate.type)) {
          const result = formBuilder.addModuleWithSingleComponent(stepId, {
            ...componentTemplate.defaultConfig,
            fieldId: undefined,
          });
          if (result) {
            formBuilder.setSelection({
              type: 'component',
              stepId,
              moduleId: result.moduleId,
              componentId: result.componentId,
            });
            if (isAddingNewComponent) {
              setTimeout(() => {
                setNewlyAddedComponentId(result.componentId);
                setIsAddingNewComponent(false);
              }, 50);
            }
          } else {
            setIsAddingNewComponent(false);
          }
          return;
        }

        const step = formBuilder.dsl.steps.find((s) => s.id === stepId);
        const targetModule = step?.modules.find((m) => m.id === moduleId);

        // Block drops onto NestedForm-locked modules
        if (
          targetModule &&
          targetModule.components.length === 1 &&
          isModuleLockingComponent(targetModule.components[0].component)
        ) {
          setIsAddingNewComponent(false);
          return;
        }

        const overIndex =
          targetModule?.components.findIndex((c) => c.id === over.id) ?? -1;

        const newComponentId = formBuilder.addComponent(
          stepId,
          moduleId,
          {
            ...componentTemplate.defaultConfig,
            id: undefined,
            fieldId: undefined,
          },
          overIndex !== -1 ? overIndex + 1 : undefined
        );

        if (newComponentId) {
          formBuilder.setSelection({
            type: 'component',
            stepId,
            moduleId,
            componentId: newComponentId,
          });

          if (isAddingNewComponent) {
            setTimeout(() => {
              setNewlyAddedComponentId(newComponentId);
              setIsAddingNewComponent(false);
            }, 50);
          }
        }
        return;
      }

      // not adding new
      setIsAddingNewComponent(false);

      // component drag cases
      if (activeData?.type === 'component') {
        const fromStepId = activeData.stepId as string;
        const fromModuleId = activeData.moduleId as string;

        if (overData?.type === 'component') {
          const toStepId = overData.stepId as string;
          const toModuleId = overData.moduleId as string;

          if (fromModuleId === toModuleId) {
            const step = formBuilder.dsl.steps.find((s) => s.id === fromStepId);
            const fromModule = step?.modules.find((m) => m.id === fromModuleId);
            if (!fromModule) return;

            const sorted = [...fromModule.components].sort(
              (a, b) => a.order - b.order
            );
            const oldIndex = sorted.findIndex((c) => c.id === active.id);
            const newIndex = sorted.findIndex((c) => c.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
              formBuilder.reorderComponents(
                fromStepId,
                fromModuleId,
                oldIndex,
                newIndex
              );
            }
          } else {
            const toStep = formBuilder.dsl.steps.find((s) => s.id === toStepId);
            const toModule = toStep?.modules.find((m) => m.id === toModuleId);
            if (!toModule) return;

            const toIndex = toModule.components.findIndex(
              (c) => c.id === over.id
            );
            formBuilder.moveComponent(
              fromStepId,
              fromModuleId,
              active.id as string,
              toStepId,
              toModuleId,
              toIndex !== -1 ? toIndex : toModule.components.length
            );
          }

          return;
        }

        if (overData?.type === 'module') {
          const toStepId = overData.stepId as string;
          const toModuleId = overData.moduleId as string;

          if (fromModuleId !== toModuleId) {
            formBuilder.moveComponent(
              fromStepId,
              fromModuleId,
              active.id as string,
              toStepId,
              toModuleId,
              0
            );
          }
          return;
        }
      }

      // module reorder
      if (activeData?.type === 'module' && overData?.type === 'module') {
        const stepId = activeData.stepId as string;

        if (stepId) {
          const step = formBuilder.dsl.steps.find((s) => s.id === stepId);
          if (!step) return;

          const sortedModules = [...step.modules].sort(
            (a, b) => a.order - b.order
          );
          const oldIndex = sortedModules.findIndex((m) => m.id === active.id);
          const newIndex = sortedModules.findIndex((m) => m.id === over.id);

          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            formBuilder.reorderModules(stepId, oldIndex, newIndex);
          }
        }
        return;
      }

      // steps reorder
      if (activeData?.type === 'step' && overData?.type === 'step') {
        const fromIndex = activeData.index as number;
        const toIndex = overData.index as number;

        if (fromIndex !== toIndex) {
          formBuilder.reorderSteps(fromIndex, toIndex);

          if (activeStepIndex === fromIndex) {
            setActiveStepIndex(toIndex);
          } else if (
            fromIndex < activeStepIndex &&
            toIndex >= activeStepIndex
          ) {
            setActiveStepIndex(activeStepIndex - 1);
          } else if (
            fromIndex > activeStepIndex &&
            toIndex <= activeStepIndex
          ) {
            setActiveStepIndex(activeStepIndex + 1);
          }
        }
      }
    },
    [formBuilder, isAddingNewComponent, activeStepIndex, setActiveStepIndex]
  );

  const handleReset = useCallback(() => {
    setShowResetConfirm(true);
  }, [setShowResetConfirm]);

  if (templateId && isLoadingTemplate) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg-tertiary">
        <Loading />
      </div>
    );
  }

  if (templateId && templateError) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-bg-tertiary gap-4">
        <Icons.AlertCircle className="w-12 h-12 text-text-error" />
        <h2 className="txt-large-medium text-text-primary">
          Failed to load template
        </h2>
        <p className="txt-medium-regular text-text-tertiary">
          The template could not be found or there was an error loading it.
        </p>
        <Button variant="secondary" onClick={handleGoToTemplates}>
          Go to Templates
        </Button>
      </div>
    );
  }

  if (!isMounted) {
    return (
      <div className="h-screen flex items-center justify-center bg-bg-tertiary">
        <Loading />
      </div>
    );
  }

  return (
    <DndContext
      sensors={readOnly ? [] : sensors}
      collisionDetection={collisionDetection}
      onDragStart={readOnly ? undefined : handleDragStart}
      onDragEnd={readOnly ? undefined : handleDragEnd}
    >
      <div className="h-screen w-full flex flex-col bg-bg-tertiary relative">
        {/* Invisible trigger zone at top of screen */}

        {/* Auto-hide header wrapper */}
        <FormBuilderHeader
          formBuilder={formBuilder}
          templateId={templateId}
          fileInputRef={fileInputRef}
          handleImport={handleImport}
          handleQuickSave={handleQuickSave}
          handleGoToTemplates={handleGoToTemplates}
          handleGoToKeys={handleGoToKeys}
          handleReset={handleReset}
          getStatusBadgeVariant={getStatusBadgeVariant}
        />

        <div className="flex-1 flex min-h-0">
          {!readOnly && (
            <>
              <div
                className="shrink-0 h-full overflow-hidden"
                style={{ width: leftWidth }}
              >
                <ComponentPalette
                  formBuilder={formBuilder}
                  onComponentAdded={(id) => {
                    setTimeout(() => setNewlyAddedComponentId(id), 50);
                  }}
                />
              </div>

              <ResizeHandle
                onMouseDown={handleMouseDown(ResizablePanelDirection.LEFT)}
              />
            </>
          )}

          <fieldset disabled={readOnly} className="contents">
            <div className="flex-1 min-w-0 h-full overflow-hidden">
              <FormCanvas
                formBuilder={formBuilder}
                activeStepIndex={activeStepIndex}
                onStepChange={setActiveStepIndex}
                newlyAddedComponentId={newlyAddedComponentId}
                newlyAddedModuleId={newlyAddedModuleId}
                onAddStepClick={readOnly ? () => {} : handleAddStepClick}
              />
            </div>

            <ResizeHandle
              onMouseDown={handleMouseDown(ResizablePanelDirection.RIGHT)}
            />

            <div
              className="shrink-0 h-full overflow-hidden"
              style={{ width: rightWidth }}
            >
              <PropertyEditor formBuilder={formBuilder} />
            </div>
          </fieldset>
        </div>

        <DragOverlay dropAnimation={FORM_BUILDER_DROP_ANIMATION}>
          <DragOverlayContent draggedItem={draggedItem} />
        </DragOverlay>
      </div>

      <ResetTemplateDialog
        open={showResetConfirm}
        onOpenChange={setShowResetConfirm}
        resetFormAndClearTemplateId={resetFormAndClearTemplateId}
      />

      <AddStepDialog
        open={showAddStepDialog}
        onOpenChange={setShowAddStepDialog}
        onAddNewStep={handleAddNewStep}
        onAddSavedStep={handleAddSavedStep}
      />

      <SaveTemplateDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        metadata={formBuilder.templateMetadata}
        saveDialogData={saveDialogData}
        setSaveDialogData={setSaveDialogData}
        onSave={handleSaveTemplate}
      />
    </DndContext>
  );
}
